import type { ClientJob, DashboardSummary } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  CircleCheck,
  CircleDashed,
  Cpu,
  RefreshCw,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { ErrorState } from '../components/ErrorState.js';
import { PageHeader } from '../components/PageHeader.js';
import { Skeleton } from '../components/Skeleton.js';
import { TotpRequiredBanner } from '../features/account/TotpRequiredBanner.js';
import { Bar, Strip } from '../features/dashboard/marks.js';
import { formatShare, SimStatusChart, statusSlices } from '../features/dashboard/SimStatusChart.js';
import { TONE_BG, TONE_TEXT, type ChartTone } from '../features/dashboard/tones.js';
import { JobSummary } from '../layout/JobDrawer.js';
import { apiRequest } from '../lib/apiClient.js';

/** Enough to answer "did my thing run?" without scrolling past the fold. */
const RECENT_JOB_LIMIT = 5;

/**
 * The one data-bearing screen in Phase 04 (plan F7). It exists to prove the
 * data layer end to end; the SIM screens are Phase 05.
 *
 * **Degrades per section, not per page.** `recentJobs` is `null` — not `[]` —
 * when `jobs.tracking` is off, because "we did not look" and "there is nothing"
 * are different answers: the first hides the panel, the second shows an empty
 * state. Collapsing them would tell a customer who has not bought job tracking
 * that they have no activity.
 *
 * **Every panel here earns one graphic, and every graphic is drawn from a
 * number the payload actually contains.** There is no sparkline, no trend
 * arrow and no period-over-period delta anywhere on this screen, because
 * `AccountStats` is a *snapshot*: the API exposes no history behind any figure
 * on it. A twelve-point line drawn from one reading is a confident picture of a
 * fact we do not have — the identical failure mode `usageMeter()` returns
 * `null` to avoid. If trends are wanted they need a stats-history endpoint
 * first, which is an API change and not a visual one.
 */
export function DashboardPage(): ReactElement {
  const { tenant } = useAuth();

  const summary = useQuery<DashboardSummary>({
    queryKey: ['dashboard', 'summary', tenant?.ref ?? null],
    queryFn: () => apiRequest<DashboardSummary>('/dashboard/summary'),
  });

  /**
   * Real freshness, not decoration. A polled console that never says when it
   * last heard from the network trains people to trust a stale number; the
   * timestamp is the query's own, so it cannot claim to be newer than the data.
   */
  const fetchedAt = summary.dataUpdatedAt > 0 ? new Date(summary.dataUpdatedAt) : null;

  return (
    <>
      <PageHeader
        title="Overview"
        description={tenant?.name ?? undefined}
        actions={
          <div className="flex items-center gap-[var(--space-3)]">
            {fetchedAt ? (
              <p className="text-[length:var(--text-2xs)] tabular-nums text-muted">
                as of {fetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            ) : null}
            <Button
              variant="ghost"
              loading={summary.isFetching}
              onClick={() => void summary.refetch()}
            >
              <RefreshCw aria-hidden="true" size={14} strokeWidth={2} />
              Refresh
            </Button>
          </div>
        }
      />

      {/* §B7 soft enforcement: the first screen after sign-in is where the
          enrolment nudge has to live, or it is never seen. */}
      <TotpRequiredBanner />

      {summary.isPending ? (
        // The skeleton is the shape of the band it replaces — one frame, four
        // cells, a label line, a numeral and a graphic — so nothing jumps when
        // the answer lands.
        <div className="card grid grid-cols-2 divide-x divide-y divide-line overflow-hidden sm:grid-cols-4 sm:divide-y-0">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col px-[var(--space-5)] py-[var(--space-5)]">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-[var(--space-2)] h-8 w-16" />
              <Skeleton className="mt-[var(--space-4)] h-2 w-full" />
            </div>
          ))}
        </div>
      ) : null}

      {summary.isError ? (
        // The shell has already rendered; only this section reports the
        // failure. Criterion 24: a vendor outage must not blank the page.
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}

      {summary.data ? (
        <>
          <StatBand stats={summary.data.stats} />

          {/*
            The composition chart and the activity feed are the two halves of
            "what is my estate doing / what has it just done", so they sit in
            one region side by side on a wide screen and stack on a narrow one.

            The chart takes two columns and the feed one: the chart now carries
            a strip, a ring and up to eight labelled rows, while a feed of five
            job cards is a column of short lines that reads worse the wider it
            gets.
          */}
          <div className="mt-[var(--space-8)] grid items-start gap-[var(--space-6)] lg:grid-cols-3">
            <section aria-labelledby="composition-heading" className="lg:col-span-2">
              <h2 id="composition-heading" className="sr-only">
                Estate by status
              </h2>
              <SimStatusChart stats={summary.data.stats} />
            </section>

            {summary.data.recentJobs !== null ? (
              <ActivityPanel jobs={summary.data.recentJobs} />
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * The stat band.
 * ------------------------------------------------------------------ */

/**
 * Four tiles in **one** frame with dividers, not four floating cards — four
 * boxes with no relationship to each other is what the client saw and called
 * basic. What is new is the third line: each figure now says what it is a
 * fraction *of*, because "1,240 active" is a number and "1,240 active, 62% of
 * the estate" is an answer.
 */
function StatBand({ stats }: { stats: DashboardSummary['stats'] }): ReactElement {
  const total = stats.totalSims;
  /** Guarded once, here: an empty estate draws no graphic rather than `NaN%`. */
  const share = (value: number): number | null => (total > 0 ? (value / total) * 100 : null);
  const slices = statusSlices(stats);

  return (
    <section aria-labelledby="stats-heading">
      <h2 id="stats-heading" className="sr-only">
        SIM totals
      </h2>
      {/*
        `.card` rather than the four utilities it replaces. The stat band, the
        composition panel, the activity feed and every table frame in the app
        are the same object — one bright plane, one border weight, one radius —
        and saying so in one class is what stops the four from drifting a radius
        apart on the next edit. The band's own geometry (the grid and the
        dividers between cells) stays here, because that IS this component.
      */}
      <div className="card grid grid-cols-2 divide-x divide-y divide-line overflow-hidden sm:grid-cols-4 sm:divide-y-0">
        <Stat label="Total SIMs" value={total} icon={Cpu} iconClass="text-muted">
          {/*
            The masthead figure gets the composition rather than a share of
            itself — 100% of the total is not information. At 6px it is a
            footnote to the numeral that says "and here is what it is made of",
            pointing at the panel below where every segment is named.
          */}
          {slices.length > 0 ? (
            <>
              <Strip slices={slices} height="sm" />
              <p className="text-[length:var(--text-2xs)] tabular-nums text-muted">
                across {slices.length} {slices.length === 1 ? 'status' : 'statuses'}
              </p>
            </>
          ) : null}
        </Stat>

        <Stat
          label="Active"
          value={stats.activeSims}
          icon={CircleCheck}
          iconClass={TONE_TEXT.active}
        >
          <ShareRail share={share(stats.activeSims)} tone="active" />
        </Stat>

        <Stat
          label="Suspended"
          value={stats.suspendedSims}
          icon={TriangleAlert}
          iconClass={TONE_TEXT.attention}
        >
          <ShareRail share={share(stats.suspendedSims)} tone="attention" />
        </Stat>

        {/*
          A figure, not a control.

          This tile used to link into activation, on the argument that the count
          of SIMs ready to activate IS the entry point. It is not: activation is
          performed against a particular SIM, and the operator reaches it from
          the row that owns it. A dashboard stat that navigates is a surprise on
          a screen whose other eight tiles do not.
        */}
        <Stat
          label="Available"
          value={stats.availableSims}
          icon={CircleDashed}
          iconClass={TONE_TEXT.idle}
        >
          <ShareRail share={share(stats.availableSims)} tone="idle" />
        </Stat>
      </div>
    </section>
  );
}

/**
 * A stat tile. §16.2: the single large number is `--text-2xl`, the label is
 * small and muted above it beside a tone-matched glyph, and the graphic sits
 * under the numeral where it reads as supporting evidence rather than as a
 * second figure.
 *
 * `tabular-nums` is deliberately **not** used on the numeral: tabular figures
 * give every digit a zero's width, which reads loose at 24px. It stays in the
 * table and in the chart's count column, where digits genuinely align down a
 * column.
 */
function Stat({
  label,
  value,
  icon: Icon,
  iconClass,
  trailing,
  children,
}: {
  label: string;
  value: number | null;
  icon: LucideIcon;
  /** A `text-chart-*` class from `tones.ts`, or `text-muted` for a total. */
  iconClass: string;
  /** A mark at the far end of the label row — the linked tile's chevron. */
  trailing?: ReactNode;
  children?: ReactNode;
}): ReactElement {
  return (
    /*
      Three bands with deliberately unequal air between them: label, then the
      figure, then the evidence. The figure is the only heavy thing in the tile
      and it is given the most room around it, which is the whole of "make the
      numbers the hero" — at `--text-2xl` the rank has to come from space and
      tracking, because §16.2 stops the scale here on purpose (a console is
      read, not browsed).

      The glyph lost its filled chip. A 28px tinted square beside a 24px numeral
      is a second object of the same visual weight competing for the first
      glance, and the tile only has one thing to say.
    */
    <div className="flex h-full flex-col px-[var(--space-5)] py-[var(--space-5)]">
      <div className="flex items-center gap-[var(--space-2)]">
        <Icon aria-hidden="true" size={13} strokeWidth={2.25} className={`shrink-0 ${iconClass}`} />
        <p className="min-w-0 flex-1 truncate text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
          {label}
        </p>
        {trailing}
      </div>

      <p className="mt-[var(--space-2)] text-[length:var(--text-2xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
        {value === null ? '—' : value.toLocaleString()}
      </p>

      {children ? (
        <div className="mt-auto flex flex-col gap-[var(--space-2)] pt-[var(--space-4)]">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The rail under a stat numeral, with its share written out beneath it.
 *
 * The text is not optional garnish: a bar is readable to about "a third", and
 * the difference between 28% and 34% of an estate is an operational one. The
 * graphic never carries a value that is not also a word.
 *
 * `null` — an estate with no SIMs at all — draws nothing rather than an empty
 * track under `0%`.
 */
function ShareRail({
  share,
  tone,
}: {
  share: number | null;
  tone: ChartTone;
}): ReactElement | null {
  if (share === null) return null;
  return (
    <>
      <Bar share={share} tone={tone} />
      <p className="text-[length:var(--text-2xs)] tabular-nums text-muted">
        {formatShare(share)} of estate
      </p>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Recent activity.
 * ------------------------------------------------------------------ */

/**
 * The feed.
 *
 * Rendered only when `recentJobs` is an array — a `null` never reaches here,
 * because "we did not look" hides the panel entirely (see the page comment).
 * An empty array is a real answer and gets the empty state.
 */
function ActivityPanel({ jobs }: { jobs: readonly ClientJob[] }): ReactElement {
  const shown = jobs.slice(0, RECENT_JOB_LIMIT);

  return (
    <section aria-labelledby="activity-heading" className="lg:col-span-1">
      <div className="card overflow-hidden">
        <div className="border-b border-line px-[var(--space-5)] py-[var(--space-3)]">
          <h2
            id="activity-heading"
            className="text-[length:var(--text-lg)] font-semibold tracking-[var(--tracking-tight)] text-body"
          >
            Recent activity
          </h2>
        </div>

        {shown.length === 0 ? (
          <div className="p-[var(--space-5)]">
            {/*
              Scoped to the two families this strip can hold: `GET /dashboard`
              narrows it to route 21's vocabulary, so a plan change or a
              whitelist update your team ran is genuinely not coming — and
              "Activations and other changes will show up here" told an
              operator who had just run one to submit it again.
            */}
            <EmptyState
              title="No activations or swaps yet"
              body="Activations and SIM swaps your team runs show up here."
            />
          </div>
        ) : (
          <>
            {/*
              One frame, divided rows — not N floating cards. Capped at
              RECENT_JOB_LIMIT so the screen has a bottom: an unbounded list
              scrolls past the fold and stops being a summary.

              The rows deliberately do **not** animate in or reorder with a
              transition. This list is polled, and a list that moves under a
              reader is worst exactly when they are reading a ref off it.
            */}
            <ul className="divide-y divide-line">
              {shown.map((job, index) => (
                <ActivityRow
                  key={job.ref}
                  job={job}
                  first={index === 0}
                  last={index === shown.length - 1}
                />
              ))}
            </ul>
            {jobs.length > RECENT_JOB_LIMIT ? (
              <div className="border-t border-line px-[var(--space-5)] py-[var(--space-3)]">
                <Link
                  to="/jobs"
                  className="inline-flex items-center gap-1 text-[length:var(--text-sm)] text-primary underline-offset-2 hover:underline"
                >
                  View all activity
                  <ChevronRight aria-hidden="true" size={14} strokeWidth={2} />
                </Link>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * A row, on a timeline rail.
 *
 * **The dot encodes `terminal` and nothing else.** It is tempting to colour it
 * by outcome so the feed is scannable by edge, and wrong: the rule that
 * `EXPIRED` is *not* a failure lives in `describeJob()` and criterion 18 pins
 * that function to `JobStatusBadge`. A second, local reading of a job's status
 * here is exactly the drift that criterion exists to stop — the dashboard
 * saying one thing and the job list another about the same job. `terminal` is a
 * field the DTO states outright, so the dot claims only what it can know:
 * still running, or finished. The outcome stays with the pill, in words,
 * rendered by the one component allowed to decide it.
 */
function ActivityRow({
  job,
  first,
  last,
}: {
  job: ClientJob;
  first: boolean;
  last: boolean;
}): ReactElement {
  const tone: ChartTone = job.terminal ? 'idle' : 'progress';

  /*
    The rail stops at the first and last dot rather than running to the edge of
    the frame: a line that starts above the first event implies an event above
    it. Written as whole literal class strings because Tailwind scans source
    text — an interpolated `top-[…]` is never generated.
  */
  const rail =
    first && last
      ? null
      : first
        ? 'top-[1.3rem] bottom-0'
        : last
          ? 'top-0 h-[1.3rem]'
          : 'inset-y-0';

  return (
    <li className="relative flex gap-[var(--space-3)] px-[var(--space-5)] py-[var(--space-3)]">
      {rail ? (
        <span aria-hidden="true" className={`absolute left-[1.4375rem] w-px bg-line ${rail}`} />
      ) : null}
      <span
        aria-hidden="true"
        // The 2px surface ring is what makes the dot punch through the rail
        // instead of sitting on it.
        className={`relative mt-[0.3rem] size-2 shrink-0 rounded-[var(--radius-pill)] ring-2 ring-surface ${TONE_BG[tone]}`}
      />
      <div className="min-w-0 flex-1">
        <JobSummary job={job} compact />
      </div>
    </li>
  );
}
